import type { MentionRow } from './generator';
import { keywordMatches } from './keywords';
import { postSummary, postUrl } from './posts';
import { rowId, type RowId } from './sentimentOverrides';

/**
 * `Configure campaigns` → `Add by keywords` (2068:29797) and the results it
 * lands on (2068:31496): which posts a set of campaign keywords finds.
 *
 * In the data layer, beside `keywords.ts` and `posts.ts`, because what a
 * keyword matches is a claim about the corpus and should be checkable without
 * React. The modal only lays the answer out.
 */

export type CampaignPost = {
  id: RowId;
  /** One line, composed from the row — see `postSummary` for why. */
  summary: string;
  /**
   * The keyword **as the user typed it** that found this post — the frame's
   * `Keyword` column reads `mygp`, lower case, which is the query rather than the
   * corpus's own label (`MyGP`). The first typed keyword that matches wins, so
   * the column reads in the order the keywords were entered.
   */
  keyword: string;
  engagement: number;
  /**
   * Where the post lives, for the `View` on its row — see `postUrl`, which
   * invents it, because the corpus carries no permalinks.
   */
  url: string;
};

/**
 * Figma `Official page posts only`.
 *
 * **The corpus has no official page posts** — it models mentions *about* a
 * brand, not posts *by* a brand's own page. The nearest thing it does model is
 * `origin`: whether a mention arose on its own or was placed (paid or seeded).
 * A brand's campaign posts are placed content, so `seeded` stands in for
 * "official" (settled with the user 2026-09-20). One function, so the day the
 * corpus grows a real field this is the only line that changes.
 */
export function isOfficialPost(row: MentionRow): boolean {
  return row.origin === 'seeded';
}

/** Trimmed, de-duplicated case-insensitively, empties dropped, order kept. */
export function normaliseKeywords(keywords: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of keywords) {
    const keyword = raw.trim();
    const key = keyword.toLocaleLowerCase();
    if (keyword === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(keyword);
  }
  return out;
}

/**
 * Every post in `rows` that any keyword matches, highest engagement first.
 *
 * - **A post matches if any keyword does** — the field is a list of things the
 *   campaign is about, and a post about one of them is a campaign post. All-of
 *   would make every added keyword *shrink* the results, which is the opposite
 *   of what adding a keyword to a campaign means.
 * - **Matching is `keywordMatches`**, the same test Trends' keyword search uses:
 *   substring, case-insensitive, against both the corpus label and its English
 *   gloss. So `network` finds `নেটওয়ার্ক`, and the two keyword features cannot
 *   disagree about what a word finds.
 * - **Ordered highest engagement first**, which is the frame's `Engagement`
 *   header drawn with `Arrow=Down`. Ties go to the newer post, then to corpus
 *   order, so the list is identical on every render — `postList`'s rule.
 *
 * `rows` is whatever the caller hands in; on a report that is the report's own
 * date range and chips, across every brand the `Brands` chip allows.
 */
export function campaignPosts(
  rows: readonly MentionRow[],
  keywords: readonly string[],
  officialOnly: boolean,
): CampaignPost[] {
  const terms = normaliseKeywords(keywords);
  if (terms.length === 0) return [];

  const found: { row: MentionRow; keyword: string; index: number }[] = [];
  rows.forEach((row, index) => {
    if (officialOnly && !isOfficialPost(row)) return;
    const keyword = terms.find((term) => keywordMatches(row.keyword, term));
    if (keyword !== undefined) found.push({ row, keyword, index });
  });

  return found
    .sort(
      (a, b) =>
        b.row.engagement - a.row.engagement || b.row.day - a.row.day || a.index - b.index,
    )
    .map(({ row, keyword }) => ({
      id: rowId(row),
      summary: postSummary(row),
      keyword,
      engagement: row.engagement,
      url: postUrl(row),
    }));
}
